import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, ExternalLink, Star, Archive, Loader2, Link2, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { cn } from '../../../lib/utils';

type OrderActionsMenuProps = {
  orderId: string;
  isFavorited?: boolean;
  isTogglingFavorite?: boolean;
  onToggleFavorite?: () => void;
  onArchive?: () => void;
  showArchive?: boolean;
};

export function OrderActionsMenu({
  orderId,
  isFavorited = false,
  isTogglingFavorite = false,
  onToggleFavorite,
  onArchive,
  showArchive = false,
}: OrderActionsMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);

  const handleOpen = () => {
    navigate(`/services/order/${orderId}`);
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/services/order/${orderId}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={handleOpen}>
          <ExternalLink className="mr-2 h-4 w-4" />
          {t('orderActions.open')}
        </DropdownMenuItem>

        <DropdownMenuItem onClick={handleCopyLink}>
          {linkCopied ? (
            <Check className="mr-2 h-4 w-4 text-green-500" />
          ) : (
            <Link2 className="mr-2 h-4 w-4" />
          )}
          {linkCopied ? t('orderActions.linkCopied') : t('orderActions.copyLink')}
        </DropdownMenuItem>
        
        {onToggleFavorite && (
          <DropdownMenuItem 
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            disabled={isTogglingFavorite}
          >
            {isTogglingFavorite ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Star className={cn(
                "mr-2 h-4 w-4",
                isFavorited && "fill-amber-500 text-amber-500"
              )} />
            )}
            {isFavorited ? t('orderActions.removeFromFavorites') : t('orderActions.addToFavorites')}
          </DropdownMenuItem>
        )}
        
        {showArchive && onArchive && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onArchive}>
              <Archive className="mr-2 h-4 w-4" />
              {t('orderActions.archive')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
